import type { ActivityEntry, ActivityResponse } from '../../../services/rewards/types';
import type { IRewardsService } from '../../../services/rewards/IRewardsService';

export interface ActivityState {
  entries: ActivityEntry[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
}

const PAGE_SIZE = 50;

type Listener = (state: ActivityState) => void;

export class ActivityManager {
  private state: ActivityState = {
    entries: [],
    loading: true,
    loadingMore: false,
    hasMore: true,
    error: null,
  };
  private offset = 0;
  private generation = 0;
  private listeners: Listener[] = [];
  private destroyed = false;

  constructor(private readonly service: IRewardsService) {}

  getState(): ActivityState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  async fetchInitial(): Promise<void> {
    const gen = ++this.generation;
    this.offset = 0;
    this.patch({ loading: true, error: null, entries: [] });
    try {
      const res = await this.service.getActivity(PAGE_SIZE, 0);
      if (this.destroyed || gen !== this.generation) return;
      this.offset = res.entries.length;
      this.patch({
        entries: res.entries,
        loading: false,
        hasMore: res.entries.length >= PAGE_SIZE,
        error: null,
      });
    } catch (err) {
      if (this.destroyed || gen !== this.generation) return;
      this.patch({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load activity',
      });
    }
  }

  async loadMore(): Promise<void> {
    if (this.state.loadingMore || !this.state.hasMore) return;
    const gen = this.generation;
    this.patch({ loadingMore: true });
    try {
      const res = await this.service.getActivity(PAGE_SIZE, this.offset);
      if (this.destroyed || gen !== this.generation) return;
      this.offset += res.entries.length;
      this.patch({
        entries: [...this.state.entries, ...res.entries],
        loadingMore: false,
        hasMore: res.entries.length >= PAGE_SIZE,
      });
    } catch (err) {
      if (this.destroyed || gen !== this.generation) return;
      this.patch({
        loadingMore: false,
        error: err instanceof Error ? err.message : 'Failed to load more',
      });
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.listeners = [];
  }

  private patch(partial: Partial<ActivityState>): void {
    this.state = { ...this.state, ...partial };
    this.emit();
  }

  private emit(): void {
    const snapshot = this.state;
    this.listeners.forEach((fn) => fn(snapshot));
  }
}
